import React, { useState, useEffect, useRef, ReactNode } from 'react';
import { useRouter } from 'next/router';
import { Box, Typography } from '@mui/material';
import {
  useAuth,
  getLmaSessionEndedByAdminHref,
  LMA_ACTIVE_SESSION_EXPIRES_AT_KEY,
  LMA_AUTH_SESSION_KEY,
  LMA_USER_REGISTERED_SESSION_KEY,
  LMA_USERNAME_LOCAL_KEY,
} from 'contexts/AuthContext';
import { registerActiveUser, isActiveUserMember } from 'src/services/leadManagementApi';
import { logger } from '../../../logger';
import {
  LMAUnauthorizedView,
  resolveCapacityAccessDeniedFields,
  type AccessDeniedFields,
} from './LMAUnauthorizedView';

function scheduleLmaSessionExpiry(ttlSeconds: number): void {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(LMA_ACTIVE_SESSION_EXPIRES_AT_KEY, String(Date.now() + ttlSeconds * 1000));
}

type GateStatus = 'checking' | 'allowed' | 'denied';

interface LMAConcurrentSessionGateProps {
  children: ReactNode;
  maxConcurrentUsers: number;
  userSessionTtlSeconds: number;
  accessDeniedFields?: AccessDeniedFields;
}

/**
 * Blocks all LMA content until Redis registration succeeds and both session token +
 * lma-user-registered are present. Authorization already filters out SuperAdmin and
 * disabled-limit scenarios before this gate is mounted.
 */
export function LMAConcurrentSessionGate({
  children,
  maxConcurrentUsers,
  userSessionTtlSeconds,
  accessDeniedFields,
}: LMAConcurrentSessionGateProps): JSX.Element {
  const router = useRouter();
  const { userName: authUserName, isLoading: isAuthLoading, isAuthenticated } = useAuth();

  const [status, setStatus] = useState<GateStatus>('checking');
  const sessionRefreshTimeoutRef = useRef<number | null>(null);

  const username =
    authUserName?.trim() ||
    (typeof window !== 'undefined' ? localStorage.getItem(LMA_USERNAME_LOCAL_KEY) : null) ||
    '';

  useEffect(() => {
    if (isAuthLoading || !isAuthenticated) {
      setStatus('checking');
      return;
    }

    if (!username) {
      setStatus('denied');
      return;
    }

    let cancelled = false;

    const run = async () => {
      setStatus('checking');

      const token =
        typeof window !== 'undefined' ? sessionStorage.getItem(LMA_AUTH_SESSION_KEY) : null;
      if (!token) {
        if (!cancelled) setStatus('denied');
        return;
      }

      try {
        if (sessionStorage.getItem(LMA_USER_REGISTERED_SESSION_KEY) === 'true') {
          try {
            const stillInSet = await isActiveUserMember(username);
            if (stillInSet) {
              // Actively renew Redis TTL for existing members (not just sessionStorage expiry).
              const renewResult = await registerActiveUser(
                username,
                maxConcurrentUsers,
                userSessionTtlSeconds
              );
              if (cancelled) return;
              if (renewResult.kickBlocked) {
                sessionStorage.removeItem(LMA_USER_REGISTERED_SESSION_KEY);
                sessionStorage.removeItem(LMA_ACTIVE_SESSION_EXPIRES_AT_KEY);
                if (typeof window !== 'undefined') {
                  window.location.href = getLmaSessionEndedByAdminHref();
                }
                return;
              }
              if (renewResult.limitReached) {
                sessionStorage.removeItem(LMA_USER_REGISTERED_SESSION_KEY);
                sessionStorage.removeItem(LMA_ACTIVE_SESSION_EXPIRES_AT_KEY);
                setStatus('denied');
                return;
              }
              sessionStorage.setItem(LMA_USER_REGISTERED_SESSION_KEY, 'true');
              scheduleLmaSessionExpiry(userSessionTtlSeconds);
              if (
                !cancelled &&
                sessionStorage.getItem(LMA_AUTH_SESSION_KEY) &&
                sessionStorage.getItem(LMA_USER_REGISTERED_SESSION_KEY) === 'true'
              ) {
                setStatus('allowed');
              }
              return;
            }
          } catch {
            // fall through to re-register
          }
          sessionStorage.removeItem(LMA_USER_REGISTERED_SESSION_KEY);
          sessionStorage.removeItem(LMA_ACTIVE_SESSION_EXPIRES_AT_KEY);
        }

        const result = await registerActiveUser(
          username,
          maxConcurrentUsers,
          userSessionTtlSeconds
        );

        if (cancelled) return;

        if (result.limitReached) {
          setStatus('denied');
          return;
        }

        if (result.kickBlocked) {
          sessionStorage.removeItem(LMA_USER_REGISTERED_SESSION_KEY);
          sessionStorage.removeItem(LMA_ACTIVE_SESSION_EXPIRES_AT_KEY);
          if (!cancelled && typeof window !== 'undefined') {
            window.location.href = getLmaSessionEndedByAdminHref();
          }
          return;
        }

        sessionStorage.setItem(LMA_USER_REGISTERED_SESSION_KEY, 'true');
        scheduleLmaSessionExpiry(userSessionTtlSeconds);
        if (
          sessionStorage.getItem(LMA_AUTH_SESSION_KEY) &&
          sessionStorage.getItem(LMA_USER_REGISTERED_SESSION_KEY) === 'true'
        ) {
          setStatus('allowed');
        } else {
          setStatus('denied');
        }
      } catch (err: any) {
        logger.error('LMA concurrent session gate: registration failed', {
          error: err?.message,
          payload: {},
        });
        if (!cancelled) setStatus('denied');
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [
    isAuthLoading,
    isAuthenticated,
    username,
    maxConcurrentUsers,
    userSessionTtlSeconds,
    router.asPath,
  ]);

  /**
   * One-shot timer at (estimated expiry − 2 minutes): POST register to extend Redis TTL.
   * After success, reschedule the next one-shot from the new expiry. No fixed-interval polling.
   */
  useEffect(() => {
    if (status !== 'allowed') return;

    const ttlSec = userSessionTtlSeconds;
    const TWO_MIN_MS = 2 * 60 * 1000;

    const clearScheduledRefresh = () => {
      if (sessionRefreshTimeoutRef.current != null) {
        window.clearTimeout(sessionRefreshTimeoutRef.current);
        sessionRefreshTimeoutRef.current = null;
      }
    };

    const tryExtendSession = async (): Promise<void> => {
      if (typeof window === 'undefined') return;
      if (document.visibilityState !== 'visible') return;

      const raw = sessionStorage.getItem(LMA_ACTIVE_SESSION_EXPIRES_AT_KEY);
      if (!raw) return;
      const expiresAt = parseInt(raw, 10);
      if (!Number.isFinite(expiresAt)) return;
      const remaining = expiresAt - Date.now();
      if (remaining <= 0) return;
      if (remaining > TWO_MIN_MS) return;

      const token = sessionStorage.getItem(LMA_AUTH_SESSION_KEY);
      if (!token || !username) return;

      try {
        const result = await registerActiveUser(username, maxConcurrentUsers, ttlSec);
        if (result.success && !result.kickBlocked && !result.limitReached) {
          scheduleLmaSessionExpiry(ttlSec);
          scheduleOneShotRefresh();
        }
        if (result.kickBlocked) {
          sessionStorage.removeItem(LMA_USER_REGISTERED_SESSION_KEY);
          sessionStorage.removeItem(LMA_ACTIVE_SESSION_EXPIRES_AT_KEY);
          clearScheduledRefresh();
          window.location.href = getLmaSessionEndedByAdminHref();
        }
      } catch (err: unknown) {
        logger.warn('LMA concurrent session gate: session refresh failed', {
          error: err instanceof Error ? err.message : String(err),
        });
      }
    };

    const scheduleOneShotRefresh = () => {
      clearScheduledRefresh();
      const raw = sessionStorage.getItem(LMA_ACTIVE_SESSION_EXPIRES_AT_KEY);
      if (!raw) return;
      const expiresAt = parseInt(raw, 10);
      if (!Number.isFinite(expiresAt)) return;

      const fireAt = expiresAt - TWO_MIN_MS;
      let delayMs = fireAt - Date.now();
      if (delayMs < 0) {
        delayMs = 0;
      }

      sessionRefreshTimeoutRef.current = window.setTimeout(() => {
        sessionRefreshTimeoutRef.current = null;
        void tryExtendSession();
      }, delayMs);
    };

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const raw = sessionStorage.getItem(LMA_ACTIVE_SESSION_EXPIRES_AT_KEY);
      if (!raw) return;
      const expiresAt = parseInt(raw, 10);
      if (!Number.isFinite(expiresAt)) return;
      const remaining = expiresAt - Date.now();
      if (remaining > 0 && remaining <= TWO_MIN_MS) {
        void tryExtendSession();
      }
    };

    scheduleOneShotRefresh();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearScheduledRefresh();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [status, username, maxConcurrentUsers, userSessionTtlSeconds]);

  const LoadingScreen = (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
      }}
    >
      <Typography variant="h6" color="text.secondary">
        Loading...
      </Typography>
    </Box>
  );

  if (status === 'checking') {
    return LoadingScreen;
  }

  if (status === 'denied') {
    return (
      <LMAUnauthorizedView
        fields={resolveCapacityAccessDeniedFields(accessDeniedFields, maxConcurrentUsers)}
        icon="block"
      />
    );
  }

  const hasToken = typeof window !== 'undefined' && !!sessionStorage.getItem(LMA_AUTH_SESSION_KEY);
  const hasReg =
    typeof window !== 'undefined' &&
    sessionStorage.getItem(LMA_USER_REGISTERED_SESSION_KEY) === 'true';

  if (!hasToken || !hasReg) {
    return LoadingScreen;
  }

  return <>{children}</>;
}
